import React, { useState, useEffect, createRef, useRef } from "react";
import Select from "react-select";
import { Link } from "react-router-dom";
import _ from "lodash";
import dayjs from "dayjs";
import ReactToPrint from "react-to-print";
import { Card, Table, Tabs, Row, Col, Pagination, Input, Button, DatePicker, Space } from "antd";
import { CSVLink } from "react-csv";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileDownload, faPrint } from "@fortawesome/free-solid-svg-icons";

function TaskReport(props) {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);

  const [taskReviewFilters, setTaskReviewFilters] = useState({
    start_date: dayjs().subtract(1, "month").startOf("month"),
    end_date: dayjs().subtract(1, "month").endOf("month"),
    users: [],
    items: [],
    statuses: [],
    groupings: [{ label: "User", value: "user_name" }],
  });
  const [rawTaskReviewData, setRawTaskReviewData] = useState([]);
  const [taskReviewData, setTaskReviewData] = useState([]);

  const [formDueDateFilters, setFormDueDateFilters] = useState({ due_at: dayjs(), task_statuses: [] });
  const [formDueDateData, setFormDueDateData] = useState([]);

  const [taskSnapshotData, setTaskSnapshotData] = useState([]);
  const [filteredTaskSnapshotData, setFilteredTaskSnapshotData] = useState([]);
  const [taskSnapshotSearchTerm, setTaskSnapshotSearchTerm] = useState("");

  const printableRef1 = useRef();
  const printableRef2 = useRef();
  const printableRef3 = useRef();

  const GetItems = async () => {
    let response = await props.AxiosRequest("/api/items/get");
    setItems(
      response.data.items.map((m) => {
        return { ...m, label: m.name, value: m.id };
      }),
    );
  };
  const GetUsers = async () => {
    let response = await props.AxiosRequest("/api/users/get");
    setUsers(
      response.data.users.map((m) => {
        return { ...m, label: m.name, value: m.id };
      }),
    );
  };
  const GetTaskStatuses = async () => {
    let response = await props.AxiosRequest("/api/task-statuses/get");
    setTaskStatuses(
      response.data.task_statuses.map((m) => {
        return { ...m, label: m.name, value: m.id };
      }),
    );
  };

  const GetTaskReview = async () => {
    if (!taskReviewFilters.start_date || !taskReviewFilters.start_date) return props.MakeNotification("warning", "Please select a date range");
    let response = await props.AxiosRequest("/api/tasks/reporting/task-review", {
      ...taskReviewFilters,
      start_date: dayjs(taskReviewFilters.start_date).startOf("day").format("YYYY-MM-DD HH:mm:ss"),
      end_date: dayjs(taskReviewFilters.end_date).endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    });
    let task_data = await MakeTaskReviewData(response.data.data);
    setRawTaskReviewData(response.data.data);
    setTaskReviewData(task_data);
  };

  const MakeTaskReviewData = async (data) => {
    if (taskReviewFilters.groupings.length === 0) return data;
    let grouped_data = _.groupBy(data, (result) => taskReviewFilters.groupings.reduce((a, b) => a + result[b.value] + ", ", "").slice(0, -2));
    let grouped_data_array = _.keys(grouped_data).map((m) => {
      return { client_name: m, special: true, key: m, user_name: "Total Tasks: " + grouped_data[m].length, children: grouped_data[m] };
    });
    return grouped_data_array;
  };

  const GetFormDueDateData = async () => {
    if (!formDueDateFilters.due_at) return props.MakeNotification("warning", "Please select a Form due Date.");
    if (formDueDateFilters.task_statuses.length === 0) return props.MakeNotification("warning", "Please select a Task Status.");
    let response = await props.AxiosRequest("/api/tasks/reporting/form-due", {
      ...formDueDateFilters,
      due_at: dayjs(formDueDateFilters.due_at).startOf("day").format("YYYY-MM-DD"),
    });
    setFormDueDateData(response.data.data);
  };

  const GetTaskSnapshotData = async () => {
    let response = await props.AxiosRequest("/api/tasks/reporting/snapshot");
    setTaskSnapshotData(response.data.data);
  };
  useEffect(() => {
    GetItems();
    GetUsers();
    GetTaskStatuses();
    GetTaskSnapshotData();
    GetTaskReview();
  }, []);

  const FilterTaskSnapshotData = async () => {
    if (taskSnapshotSearchTerm?.length === 0) return setFilteredTaskSnapshotData(taskSnapshotData);
    let filtered_list = taskSnapshotData.filter((f) =>
      _.toLower(f.client_name).indexOf(_.toLower(taskSnapshotSearchTerm)) > -1 ||
      _.toLower(f.job_name).indexOf(_.toLower(taskSnapshotSearchTerm)) > -1 ||
      _.toLower(f.description).indexOf(_.toLower(taskSnapshotSearchTerm)) > -1 ||
      _.toLower(f.user_name).indexOf(_.toLower(taskSnapshotSearchTerm)) > -1 ||
      _.toLower(f.item_name).indexOf(_.toLower(taskSnapshotSearchTerm)) > -1 ||
      _.toLower(f.status_name).indexOf(_.toLower(taskSnapshotSearchTerm)) > -1
        ? true
        : false,
    );
    setFilteredTaskSnapshotData(filtered_list);
  };

  useEffect(() => {
    FilterTaskSnapshotData();
  }, [taskSnapshotData, taskSnapshotSearchTerm]);

  const task_report_columns = [
    {
      title: "Client Name",
      dataIndex: "client_name",
      key: "client_name",
      render: (value, row) => (row.special ? value : <Link to={"/client-details/" + row.tag}>{value}</Link>),
      sorter: (a, b) => (a.client_name.toUpperCase() > b.client_name.toUpperCase() ? 1 : -1),
    },
    {
      title: "Job",
      dataIndex: "job_name",
      key: "job_name",
      sorter: (a, b) => (a.job_name.toUpperCase() > b.job_name.toUpperCase() ? 1 : -1),
    },
    {
      title: "Item Category",
      dataIndex: "item_category_name",
      key: "item_category_name",
      sorter: (a, b) => (a.item_category_name.toUpperCase() > b.item_category_name.toUpperCase() ? 1 : -1),
    },
    {
      title: "Item",
      dataIndex: "item_name",
      key: "item_name",
      sorter: (a, b) => (a.item_name.toUpperCase() > b.item_name.toUpperCase() ? 1 : -1),
    },
    {
      title: "Status",
      dataIndex: "status_name",
      key: "status_name",
      sorter: (a, b) => (a.status_name.toUpperCase() > b.status_name.toUpperCase() ? 1 : -1),
    },
    {
      title: "Assignee",
      dataIndex: "user_name",
      key: "user_name",
      sorter: (a, b) => (a.user_name.toUpperCase() > b.user_name.toUpperCase() ? 1 : -1),
    },
  ];
  const form_due_date_columns = [
    {
      title: "Client Name",
      dataIndex: "client_name",
      key: "client_name",
      render: (value, row) => <Link to={"/client-details/" + row.tag}>{value}</Link>,
      sorter: (a, b) => (a.client_name.toUpperCase() > b.client_name.toUpperCase() ? 1 : -1),
    },
    {
      title: "Job",
      dataIndex: "job_name",
      key: "job_name",
      sorter: (a, b) => (a.job_name.toUpperCase() > b.job_name.toUpperCase() ? 1 : -1),
    },
    {
      title: "Task",
      dataIndex: "task_name",
      key: "task_name",
      sorter: (a, b) => (a.task_name.toUpperCase() > b.task_name.toUpperCase() ? 1 : -1),
    },
    {
      title: "Assignee",
      dataIndex: "user_name",
      key: "user_name",
      sorter: (a, b) => (a.user_name.toUpperCase() > b.user_name.toUpperCase() ? 1 : -1),
    },
    {
      title: "Task Due Date",
      dataIndex: "due_at",
      key: "due_at",
      sorter: (a, b) => (a.due_at > b.due_at ? 1 : -1),
    },
    {
      title: "Form Due Date",
      dataIndex: "form_due_date",
      key: "form_due_date",
      sorter: (a, b) => (a.form_due_date > b.form_due_date ? 1 : -1),
    },
  ];
  const task_snapshot_columns = [
    {
      title: "Client Name",
      dataIndex: "client_name",
      key: "client_name",
      render: (value, row) => <Link to={"/client-details/" + row.tag}>{value}</Link>,
      sorter: (a, b) => (a.client_name.toUpperCase() > b.client_name.toUpperCase() ? 1 : -1),
    },
    {
      title: "Job",
      dataIndex: "job_name",
      key: "job_name",
      sorter: (a, b) => (a.job_name.toUpperCase() > b.job_name.toUpperCase() ? 1 : -1),
    },
    {
      title: "Task",
      dataIndex: "description",
      key: "description",
      sorter: (a, b) => (a.description.toUpperCase() > b.description.toUpperCase() ? 1 : -1),
    },
    {
      title: "Assignee",
      dataIndex: "user_name",
      key: "user_name",
      sorter: (a, b) => (a.user_name.toUpperCase() > b.user_name.toUpperCase() ? 1 : -1),
    },
    {
      title: "Item",
      dataIndex: "item_name",
      key: "item_name",
      sorter: (a, b) => (a.item_name > b.item_name ? 1 : -1),
    },
    {
      title: "Status",
      dataIndex: "status_name",
      key: "status_name",
      sorter: (a, b) => (a.status_name > b.status_name ? 1 : -1),
    },
  ];

  const task_reports_tabs = [
    {
      label: "Task Review",
      key: 0,
      children: (
        <>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <label className="col-form-label-sm">Task Created During: </label>
              <DatePicker.RangePicker
                className="w-100"
                allowClear={false}
                format="MM/DD/YYYY"
                presets={props.default_rangepicker_presets}
                value={[taskReviewFilters.start_date, taskReviewFilters.end_date]}
                onCalendarChange={(dates) => {
                  setTaskReviewFilters({ ...taskReviewFilters, start_date: dates[0], end_date: dates[1] });
                }}
              />
              {/* <input className="form-control mb-2" type="text" id="daterange-selector" style={{ height: "38px" }} defaultValue={this.state.start_range + " - " + this.state.end_range} /> */}
            </Col>
            <Col span={6}>
              <label className="col-form-label-sm">Task Items: </label>
              <Select
                options={items}
                isMulti={true}
                value={taskReviewFilters?.items}
                onChange={(e) => {
                  setTaskReviewFilters({ ...taskReviewFilters, items: e });
                }}
              />
            </Col>
            <Col span={6}>
              <label className="col-form-label-sm">Task Assigned To: </label>
              <Select
                options={props.MakeUserOptions(users)}
                isMulti={true}
                value={taskReviewFilters?.users}
                onChange={(e) => {
                  setTaskReviewFilters({ ...taskReviewFilters, users: e });
                }}
              />
            </Col>
            <Col span={6}>
              <label className="col-form-label-sm">With the Current Status of: </label>
              <Select
                options={taskStatuses}
                isMulti={true}
                value={taskReviewFilters?.statuses}
                onChange={(e) => {
                  setTaskReviewFilters({ ...taskReviewFilters, statuses: e });
                }}
              />
            </Col>
            <Col span={6}>
              <label className="col-form-label-sm">Grouped By: </label>
              <Select
                options={[
                  { label: "User", value: "user_name" },
                  { label: "Item", value: "item_name" },
                  { label: "Status", value: "status_name" },
                  { label: "Client", value: "client_name" },
                ]}
                isMulti={true}
                value={taskReviewFilters?.groupings}
                onChange={(e) => {
                  setTaskReviewFilters({ ...taskReviewFilters, groupings: e });
                }}
              />
            </Col>

            <Col span={18} className="text-right" style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button type="primary" onClick={GetTaskReview} style={{ display: "inline-block", alignSelf: "flex-end" }}>
                Submit
              </Button>
            </Col>
            <Col span={24}>
              <hr />
            </Col>
            <Col span={24} ref={printableRef1}>
              <Table
                style={{ width: "100%" }}
                columns={task_report_columns}
                dataSource={taskReviewData}
                indentSize={20}
                footer={(current_page_data) => (
                  <Row gutter={[16, 16]}>
                    <Col span={24} className="text-right">
                      Total Tasks: {rawTaskReviewData.length}
                    </Col>
                  </Row>
                )}
              />
            </Col>

            <Col span={24} className="text-right">
              <Space>
                <CSVLink filename="TaskReview.csv" data={taskReviewData}>
                  <Button type="primary" size="small">
                    <FontAwesomeIcon icon={faFileDownload} className="mr-2" /> Download CSV File
                  </Button>
                </CSVLink>
                <ReactToPrint
                  trigger={() => (
                    <Button size="small">
                      <FontAwesomeIcon icon={faPrint} className="mr-2" /> Print Table
                    </Button>
                  )}
                  content={() => printableRef1.current}
                />
              </Space>
            </Col>
          </Row>
        </>
      ),
    },
    {
      label: "Form Due Date Report",
      key: 1,
      children: (
        <>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <label className="col-form-label-sm">Form Due Date: </label>
              <DatePicker
                className="w-100"
                value={formDueDateFilters.due_at}
                onCalendarChange={(date) => {
                  setFormDueDateFilters({ ...formDueDateFilters, due_at: date });
                }}
              />
              {/* <input className="form-control mb-2" type="text" id="daterange-selector" style={{ height: "38px" }} defaultValue={this.state.start_range + " - " + this.state.end_range} /> */}
            </Col>
            <Col span={12}>
              <label className="col-form-label-sm">With the Task Status Of... </label>
              <Select
                options={taskStatuses}
                isMulti={true}
                value={formDueDateFilters?.items}
                onChange={(e) => {
                  setFormDueDateFilters({ ...formDueDateFilters, task_statuses: e });
                }}
              />
            </Col>

            <Col span={6} className="text-right" style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button type="primary" onClick={GetFormDueDateData} style={{ display: "inline-block", alignSelf: "flex-end" }}>
                Submit
              </Button>
            </Col>
            <Col span={24}>
              <hr />
            </Col>
            <Col span={24} ref={printableRef2}>
              <Table
                style={{ width: "100%" }}
                columns={form_due_date_columns}
                dataSource={formDueDateData}
                indentSize={20}
                footer={(current_page_data) => (
                  <Row gutter={[16, 16]}>
                    <Col span={24} className="text-right">
                      Total Tasks: {formDueDateData.length}
                    </Col>
                  </Row>
                )}
              />
            </Col>
            <Col span={24} className="text-right">
              <Space>
                <CSVLink filename="FormDueDate.csv" data={formDueDateData}>
                  <Button type="primary" size="small">
                    <FontAwesomeIcon icon={faFileDownload} className="mr-2" /> Download CSV File
                  </Button>
                </CSVLink>
                <ReactToPrint
                  trigger={() => (
                    <Button size="small">
                      <FontAwesomeIcon icon={faPrint} className="mr-2" /> Print Table
                    </Button>
                  )}
                  content={() => printableRef2.current}
                />
              </Space>
            </Col>
          </Row>
        </>
      ),
    },
    {
      label: "Task Snapshot",
      key: 2,
      children: (
        <>
          <Row gutter={[16, 16]}>
            <Col span={18}></Col>
            <Col span={6} className="text-right">
              <Input
                placeholder="Search..."
                value={taskSnapshotSearchTerm}
                onChange={(e) => {
                  setTaskSnapshotSearchTerm(e.target.value);
                }}
              />
            </Col>
            <Col span={24} ref={printableRef3}>
              <Table
                style={{ width: "100%" }}
                columns={task_snapshot_columns}
                dataSource={filteredTaskSnapshotData}
                indentSize={20}
                footer={(current_page_data) => (
                  <Row gutter={[16, 16]}>
                    <Col span={24} className="text-right">
                      Total Tasks: {taskSnapshotData.length}
                    </Col>
                  </Row>
                )}
              />
            </Col>
            <Col span={24} className="text-right">
              <Space>
                <CSVLink filename="TaskSnapshot.csv" data={filteredTaskSnapshotData}>
                  <Button size="small" type="primary">
                    <FontAwesomeIcon icon={faFileDownload} className="mr-2" /> Download CSV File
                  </Button>
                </CSVLink>
                <ReactToPrint
                  trigger={() => (
                    <Button size="small">
                      <FontAwesomeIcon icon={faPrint} className="mr-2" /> Print Table
                    </Button>
                  )}
                  content={() => printableRef3.current}
                />
              </Space>
            </Col>
          </Row>
        </>
      ),
    },
  ];
  return (
    <main className="content">
      <div className="container-fluid">
        <div className="header">
          <h1 className="header-title">Task Reports</h1>
        </div>
        <Card>
          <Tabs tabPosition="left" size="large" items={task_reports_tabs} />
        </Card>
      </div>
    </main>
  );
}

export default TaskReport;
